// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import { Description } from '@alto/design-system';
import React from 'react';
import { getPricingForPrescriptionFromDeliveryItemsForShipment } from '~shared/features/delivery-info/selectors/getDeliveryItems';
import { useCartSelectedPricingInfoForPrescription } from '~shared/features/prescriptions/hooks/useCartSelectedPricingInfoForPrescription';
import { PRESCRIPTION_PRICING_LABELS } from '~shared/features/pricing/constants';
import { type PriceInfo } from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { type Pricing } from '~shared/features/pricing/types';
import { formatDollars } from '~shared/helpers/currency';
import { useOrderPricing } from '~shared/queries';
import { useSelectorShared } from '~shared/store';
import { type LightDelivery } from '~shared/types';
import { ORDER_TYPE_CONTEXT, type OrderTypeContext } from '../../../auto-refill';
import { useShipmentID } from '../../../order-details';
import { UnavailablePriceDescription } from './UnavailablePriceDescription';
import { useSavingsAppliedDescriptions } from './useSavingsAppliedDescriptions';

/**
 * Type predicate helper to narrow the type of a PriceInfo from cart pricing or Pricing from the delivery
 * https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
 */
const isPriceInfo = (priceInfo: PriceInfo | Pricing): priceInfo is PriceInfo => {
  return (priceInfo as PriceInfo).priceLabel !== undefined;
};

const descriptionListItems = (descriptions: string[]): React.ReactElement[] => {
  return descriptions.map((text) => {
    return (
      <>
        &nbsp;&bull;&nbsp;
        <Description color={COLORS.TEXT_COLORS.SUCCESS}>{text}</Description>
      </>
    );
  });
};

export const PriceDescription = ({
  orderTypeContext,
  prescriptionID,
  delivery,
}: {
  readonly orderTypeContext: OrderTypeContext;
  readonly prescriptionID: number;
  readonly delivery?: LightDelivery;
}) => {
  const isPostCheckout = orderTypeContext === ORDER_TYPE_CONTEXT.POST_CHECKOUT;
  const { shipmentID } = useShipmentID();
  const { orderPricing } = useOrderPricing({ shipmentID });
  const savingsDescriptions = useSavingsAppliedDescriptions(prescriptionID);
  const { prescriptionPricingInfo } = useCartSelectedPricingInfoForPrescription({ prescriptionID });

  const prescriptionPricing = useSelectorShared((state) => {
    if (isPostCheckout) {
      return getPricingForPrescriptionFromDeliveryItemsForShipment(state, { prescriptionID, shipmentID, orderPricing });
    }
    return prescriptionPricingInfo;
  });

  if (!prescriptionPricing) {
    return (
      <UnavailablePriceDescription
        isPostCheckout={isPostCheckout}
        prescriptionID={prescriptionID}
      />
    );
  }
  // we get prescription `PriceInfo` when we're in the cart
  if (isPriceInfo(prescriptionPricing)) {
    const { price, priceLabel } = prescriptionPricing;
    if (priceLabel === PRESCRIPTION_PRICING_LABELS.PRICE_UNAVAILABLE || !price) {
      return (
        <UnavailablePriceDescription
          isPostCheckout={false}
          prescriptionID={prescriptionID}
        />
      );
    }

    return (
      <Description>
        {priceLabel} {price}
      </Description>
    );
  }

  // we get prescription `Pricing` when we're in the delivery details
  const pricingInfo = prescriptionPricing.prices.find(({ selected }) => selected);
  const formattedPrice = formatDollars(pricingInfo?.price ?? 0);

  if (prescriptionPricing.scenario === 'unavailable') {
    return (
      <UnavailablePriceDescription
        isPostCheckout
        prescriptionID={prescriptionID}
      />
    );
  }

  let descriptions: string[] = [];
  if (prescriptionPricing.scenario === 'progyny') {
    descriptions.push('Progyny insurance applied');
  }

  if (delivery?.med_sync_status === 'short_fill') {
    descriptions.push('Paying less for partial fill');
  }

  descriptions = descriptions.concat(savingsDescriptions);

  return (
    <>
      <Description>{formattedPrice}</Description>
      {descriptionListItems(descriptions)}
    </>
  );
};
